import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getNotificationPrefs, updateNotificationPrefs } from "@/lib/digests.functions";

export const Route = createFileRoute("/_authenticated/settings/notifications")({
  component: NotificationSettings,
});

type Mode = "realtime" | "smart_digest" | "daily" | "weekly" | "off";
type Channel = "email" | "in_app" | "slack";

const CHANNELS: { key: Channel; label: string; description: string }[] = [
  { key: "in_app", label: "In-app", description: "Show digests on the Digests page." },
  { key: "email", label: "Email", description: "Deliver digests to your account email (coming soon)." },
  { key: "slack", label: "Slack", description: "Post digest summary to a Slack webhook." },
];

function NotificationSettings() {
  const qc = useQueryClient();
  const fetchPrefs = useServerFn(getNotificationPrefs);
  const update = useServerFn(updateNotificationPrefs);
  const { data } = useQuery({ queryKey: ["notif-prefs"], queryFn: () => fetchPrefs() });

  const prefByChannel = new Map<Channel, any>();
  (data?.prefs ?? []).forEach((p: any) => prefByChannel.set(p.channel, p));

  const mut = useMutation({
    mutationFn: (vars: any) => update({ data: vars }),
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["notif-prefs"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-base font-medium">Digest delivery</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose how and when you receive digests across each channel.
        </p>
      </div>
      {CHANNELS.map((c) => (
        <ChannelRow
          key={c.key}
          channel={c.key}
          label={c.label}
          description={c.description}
          existing={prefByChannel.get(c.key)}
          onSave={(prefs) => mut.mutate({ channel: c.key, prefs })}
          saving={mut.isPending}
        />
      ))}
    </div>
  );
}

function ChannelRow({
  channel,
  label,
  description,
  existing,
  onSave,
  saving,
}: {
  channel: Channel;
  label: string;
  description: string;
  existing: any;
  onSave: (p: { mode: Mode; digest_time: string | null; slack_webhook_url: string | null }) => void;
  saving: boolean;
}) {
  const [mode, setMode] = useState<Mode>(existing?.mode ?? "smart_digest");
  const [time, setTime] = useState<string>(existing?.digest_time?.slice(0, 5) ?? "09:00");
  const [webhook, setWebhook] = useState<string>(existing?.slack_webhook_url ?? "");

  useEffect(() => {
    if (existing) {
      setMode(existing.mode);
      setTime(existing.digest_time?.slice(0, 5) ?? "09:00");
      setWebhook(existing.slack_webhook_url ?? "");
    }
  }, [existing]);

  const showTime = mode === "daily" || mode === "weekly" || mode === "smart_digest";
  const showWebhook = channel === "slack";

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-sm font-medium">{label}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <Label className="text-xs">Mode</Label>
          <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Off</SelectItem>
              <SelectItem value="realtime">Realtime</SelectItem>
              <SelectItem value="smart_digest">Smart digest</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {showTime && (
          <div>
            <Label className="text-xs">Digest time (UTC)</Label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="mt-1"
            />
          </div>
        )}
        {showWebhook && (
          <div className="sm:col-span-2">
            <Label className="text-xs">Slack webhook URL</Label>
            <Input
              type="url"
              placeholder="https://hooks.slack.com/services/…"
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              className="mt-1"
            />
          </div>
        )}
      </div>
      <div className="mt-4 flex justify-end">
        <Button
          size="sm"
          disabled={saving}
          onClick={() =>
            onSave({
              mode,
              digest_time: showTime ? time : null,
              slack_webhook_url: showWebhook ? webhook || null : null,
            })
          }
        >
          Save {label}
        </Button>
      </div>
    </div>
  );
}
